import React, { useState, useMemo, useEffect } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import fetchApi from '../utils/api.js';
import { usePermissionContext } from '../contexts/PermissionContext.js';
import AirDatePicker from '../components/ui/AirDatePicker';
import SearchBar from '../components/ui/SearchBar';
import PageSkeleton from '../components/ui/PageSkeleton';
import DataTable from '../components/ui/DataTable';

function formatDate(value) {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
}

function SalesReturnList({ onAddReturn, onEditReturn, onPreviewReturn }) {
    const { hasPermission } = usePermissionContext();

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [draftStartDate, setDraftStartDate] = useState('');
    const [draftEndDate, setDraftEndDate] = useState('');
    const [appliedStartDate, setAppliedStartDate] = useState('');
    const [appliedEndDate, setAppliedEndDate] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalEntries, setTotalEntries] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

    const fetchReturns = async (searchQuery = '', currentPage = 1, limit = 10, filters = {}) => {
        if (!searchQuery) setLoading(true);
        try {
            const queryParams = new URLSearchParams();
            if (searchQuery) queryParams.append('search', searchQuery);
            queryParams.append('page', currentPage);
            queryParams.append('limit', limit);
            if (filters.startDate) queryParams.append('startDate', filters.startDate);
            if (filters.endDate) queryParams.append('endDate', filters.endDate);

            const result = await fetchApi(`/sales-return?${queryParams.toString()}`);
            if (result.salesReturns) {
                setRows(result.salesReturns);
                setTotalEntries(result.total || 0);
                setTotalPages(result.pages || 1);
            }
        } catch (error) {
            console.error("Fetch Sales Returns Error:", error);
            if (error.status !== 403) toast.error("Failed to load sales returns");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delay = setTimeout(() => {
            fetchReturns(search, page, pageSize, {
                startDate: appliedStartDate,
                endDate: appliedEndDate
            });
        }, 250);
        return () => clearTimeout(delay);
    }, [search, page, pageSize, appliedStartDate, appliedEndDate]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') direction = 'descending';
        setSortConfig({ key, direction });
    };

    const sortedRows = useMemo(() => {
        const items = [...rows];
        if (sortConfig.key) {
            items.sort((a, b) => {
                let av = a[sortConfig.key] ?? '';
                let bv = b[sortConfig.key] ?? '';
                if (sortConfig.key === 'total_amount') { av = parseFloat(av) || 0; bv = parseFloat(bv) || 0; }
                else { av = String(av).toLowerCase(); bv = String(bv).toLowerCase(); }
                if (av < bv) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (av > bv) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return items;
    }, [rows, sortConfig]);

    const showingFrom = totalEntries === 0 ? 0 : (page - 1) * pageSize + 1;
    const showingTo = Math.min(totalEntries, (page - 1) * pageSize + rows.length);

    const pages = useMemo(() => {
        const out = [];
        const max = 5;
        let start = Math.max(1, page - Math.floor(max / 2));
        let end = Math.min(totalPages, start + max - 1);
        start = Math.max(1, end - max + 1);
        for (let i = start; i <= end; i++) out.push(i);
        return out;
    }, [page, totalPages]);

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: 'Do you want to delete this sales return?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await fetchApi(`/sales-return/${id}`, { method: 'DELETE' });
                    setRows(prev => prev.filter(r => r._id !== id && r.id !== id));
                    toast.success("Sales return deleted");
                } catch (error) {
                    if (error.status !== 403) toast.error("Failed to delete");
                }
            }
        });
    };

    const onSubmitFilters = (e) => {
        e.preventDefault();
        if (!draftStartDate && draftEndDate) { toast.error("Please enter Start Date"); return; }
        setAppliedStartDate(draftStartDate);
        setAppliedEndDate(draftEndDate || (draftStartDate ? new Date().toISOString().split('T')[0] : ''));
        setPage(1);
    };

    const onResetFilters = () => {
        setDraftStartDate('');
        setDraftEndDate('');
        setAppliedStartDate('');
        setAppliedEndDate('');
        setSearch('');
        setPage(1);
    };

    const handleExcelDownload = () => {
        try {
            const worksheetData = sortedRows.map((r, idx) => ({
                'No.': idx + 1,
                'CUSTOMER': r.customer_trade_name || r.customer_name || '',
                'RETURN NO': r.return_no || '',
                'SALE NO': r.original_invoice_no || '',
                'RETURN DATE': r.return_date ? new Date(r.return_date).toLocaleDateString('en-GB') : '',
                'INVOICE TYPE': r.invoice_type_name || '',
                'AMOUNT': parseFloat(r.total_amount || 0).toFixed(2)
            }));

            const worksheet = XLSX.utils.json_to_sheet(worksheetData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Sales Returns');
            XLSX.writeFile(workbook, 'SalesReturnList.xlsx');
            toast.success("Excel exported successfully!");
        } catch (error) {
            console.error("Excel export error:", error);
            toast.error("Failed to export Excel");
        }
    };

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        const totalAmount = sortedRows.reduce((s, r) => s + (parseFloat(r.total_amount) || 0), 0);
        printWindow.document.write(`
            <html>
                <head>
                    <title>Sales Return List</title>
                    <style>
                        body { font-family: Arial, sans-serif; margin: 20px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
                        th { background-color: #f2f2f2; }
                        h2 { text-align: center; }
                        .total-row { font-weight: bold; background-color: #f9f9f9; }
                    </style>
                </head>
                <body>
                    <h2>Sales Return List</h2>
                    <table>
                        <thead>
                            <tr>
                                <th>No.</th>
                                <th>CUSTOMER</th>
                                <th>RETURN NO</th>
                                <th>SALE NO</th>
                                <th>RETURN DATE</th>
                                <th>INVOICE TYPE</th>
                                <th style="text-align: right;">AMOUNT</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${sortedRows.map((r, idx) => `
                                <tr>
                                    <td>${idx + 1}</td>
                                    <td>${r.customer_trade_name || r.customer_name || ''}</td>
                                    <td>${r.return_no || ''}</td>
                                    <td>${r.original_invoice_no || ''}</td>
                                    <td>${r.return_date ? new Date(r.return_date).toLocaleDateString('en-GB') : ''}</td>
                                    <td>${r.invoice_type_name || ''}</td>
                                    <td style="text-align: right;">₹${parseFloat(r.total_amount || 0).toFixed(2)}</td>
                                </tr>
                            `).join('')}
                            <tr class="total-row">
                                <td colspan="6" style="text-align: right;">TOTAL:</td>
                                <td style="text-align: right;">₹${totalAmount.toFixed(2)}</td>
                            </tr>
                        </tbody>
                    </table>
                </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
    };

    const thStyle = (key) => ({
        cursor: 'pointer',
        userSelect: 'none'
    });

    if (loading) return <PageSkeleton />;

    const columns = [
        {
            key: 'customer_name',
            label: 'CUSTOMER',
            sortable: true,
            render: (r) => (
                <>
                    <div style={{ fontWeight: 600 }}>{r.customer_trade_name || r.customer_name}</div>
                    {r.customer_trade_name && r.customer_name !== r.customer_trade_name && (
                        <div style={{ fontSize: '11px', color: '#666' }}>{r.customer_name}</div>
                    )}
                </>
            )
        },
        {
            key: 'return_no',
            label: 'RETURN NO',
            sortable: true,
            render: (r) => <span style={{ fontWeight: 600, color: '#0c3447' }}>{r.return_no}</span>
        },
        {
            key: 'original_invoice_no',
            label: 'SALE NO',
            sortable: true,
            render: (r) => r.original_invoice_no || '—'
        },
        {
            key: 'return_date',
            label: 'RETURN DATE',
            sortable: true,
            render: (r) => formatDate(r.return_date)
        },
        {
            key: 'invoice_type_name',
            label: 'INVOICE TYPE',
            sortable: true,
            render: (r) => r.invoice_type_name || '—'
        },
        {
            key: 'total_amount',
            label: 'AMOUNT',
            sortable: true,
            style: { textAlign: 'right' },
            cellStyle: () => ({ textAlign: 'right' }),
            render: (r) => (
                <span style={{ fontWeight: 600 }}>
                    ₹{(parseFloat(r.total_amount) || 0).toFixed(2)}
                </span>
            )
        },
        {
            key: 'preview',
            label: 'PREVIEW',
            style: { textAlign: 'center' },
            render: (r) => (
                <button
                    type="button"
                    style={{
                        padding: '4px 12px',
                        backgroundColor: '#0c3447',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '12px',
                        margin: '0 auto',
                        display: 'block'
                    }}
                    onClick={() => onPreviewReturn && onPreviewReturn(r)}
                >
                    Preview
                </button>
            )
        },
        ...((hasPermission('SalesReturn', 'update') || hasPermission('SalesReturn', 'delete')) ? [{
            key: 'actions',
            label: 'ACTIONS',
            render: (r) => (
                <div className="list-page-action-icons">
                    {hasPermission('SalesReturn', 'update') && (
                        <button
                            type="button"
                            className="list-page-icon-btn"
                            title="Edit"
                            onClick={() => onEditReturn && onEditReturn(r)}
                            style={{ marginRight: '8px' }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                        </button>
                    )}
                    {hasPermission('SalesReturn', 'delete') && (
                        <button
                            type="button"
                            className="list-page-icon-btn"
                            title="Delete"
                            onClick={() => handleDelete(r._id || r.id)}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                        </button>
                    )}
                </div>
            )
        }] : [])
    ];

    const footerRow = sortedRows.length > 0 ? (
        <tr style={{ backgroundColor: '#eaeaea', fontWeight: 'bold' }}>
            <td colSpan={5} style={{ textAlign: 'right', padding: '12px 10px', color: '#111827' }}>TOTAL</td>
            <td style={{ fontWeight: 'bold', padding: '12px 10px', textAlign: 'right', color: '#111827' }}>
                ₹{sortedRows.reduce((s, r) => s + (parseFloat(r.total_amount) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </td>
            <td></td>
            {(hasPermission('SalesReturn', 'update') || hasPermission('SalesReturn', 'delete')) && <td></td>}
        </tr>
    ) : null;

    return (
        <div className="list-page-container">
            {/* Header */}
            <div className="list-page-header">
                <h1 className="list-page-title">Sales Return List</h1>
                <div className="list-page-action-buttons">
                    {hasPermission('SalesReturn', 'report') && (
                        <>
                            <button className="list-page-btn list-page-btn-excel" onClick={handleExcelDownload}>
                                EXCEL
                            </button>
                            <button className="list-page-btn list-page-btn-print" onClick={handlePrint}>
                                PRINT
                            </button>
                        </>
                    )}
                    {hasPermission('SalesReturn', 'add') && (
                        <button className="list-page-btn list-page-btn-add" onClick={() => onAddReturn && onAddReturn()}>
                            ADD RETURN
                        </button>
                    )}
                </div>
            </div>

            <DataTable
                columns={columns}
                data={sortedRows}
                sortConfig={sortConfig}
                onSort={requestSort}
                loading={loading}
                emptyMessage="No records found"
                page={page}
                pageSize={pageSize}
                totalEntries={totalEntries}
                totalPages={totalPages}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                footerRow={footerRow}
                searchBar={
                    <>
                        <SearchBar
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            placeholder="Search return no, customer, invoice no..."
                        />
                        <div className="list-page-filter-box">
                            <div className="list-page-filter-group">
                                <label className="list-page-filter-label">Start Date</label>
                                <AirDatePicker
                                    className="list-page-filter-date"
                                    value={draftStartDate}
                                    onChange={(val) => setDraftStartDate(val)}
                                    placeholder="Start Date"
                                />
                            </div>
                            <div className="list-page-filter-group">
                                <label className="list-page-filter-label">End Date</label>
                                <AirDatePicker
                                    className="list-page-filter-date"
                                    value={draftEndDate}
                                    onChange={(val) => setDraftEndDate(val)}
                                    placeholder="End Date"
                                />
                            </div>
                            <button className="list-page-btn list-page-btn-submit" onClick={onSubmitFilters}>Submit</button>
                            <button className="list-page-btn list-page-btn-reset" onClick={onResetFilters}>Reset</button>
                        </div>
                    </>
                }
            />
        </div>
    );
}

export default SalesReturnList;
